import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import ComponentCard from "../../components/common/ComponentCard";
import Form from "../../components/form/Form";
import Label from "../../components/form/Label";
import Input from "../../components/form/input/InputField";
import Select from "../../components/form/Select";
import Button from "../../components/ui/button/Button";

const apiUrl = process.env.REACT_APP_API_URL;

const resolveDietaryId = (dietary: any): string =>
  String(dietary?.dietary_id ?? dietary?.id ?? "");

const resolveDietaryName = (dietary: any): string =>
  String(dietary?.name ?? dietary?.dietary_name ?? "");

const resolveStatus = (dietary: any): string => {
  const raw = dietary?.status;
  if (raw === 1 || raw === "1" || raw === "current") return "current";
  if (raw === 2 || raw === "2" || raw === "non-current" || raw === "non_current") {
    return "non-current";
  }
  return "current";
};

export default function UpdateDietary() {
  const navigate = useNavigate();
  const location = useLocation();
  const dietary = location.state?.dietary;

  const [formData, setFormData] = useState({
    dietaryName: "",
    status: "current",
  });
  const [errors, setErrors] = useState({
    dietaryName: "",
  });
  const [submitError, setSubmitError] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const dietaryId = resolveDietaryId(dietary);

  useEffect(() => {
    if (!dietary) return;
    setFormData({
      dietaryName: resolveDietaryName(dietary),
      status: resolveStatus(dietary),
    });
  }, [dietary]);

  const validate = () => {
    const nextErrors = { dietaryName: "" };
    if (!formData.dietaryName.trim()) {
      nextErrors.dietaryName = "Dietary name is required.";
    }
    setErrors(nextErrors);
    return Object.values(nextErrors).every((value) => !value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    if (!dietaryId) {
      setSubmitError("No dietary selected. Please open a dietary from the list.");
      return;
    }
    if (!apiUrl) {
      setSubmitError("API URL is not configured.");
      return;
    }

    setSubmitError("");
    setSubmitLoading(true);
    try {
      const token = localStorage.getItem("token");
      const payload = {
        dietary_id: Number.isNaN(Number(dietaryId)) ? dietaryId : Number(dietaryId),
        name: formData.dietaryName,
        status: formData.status,
      };

      const response = await fetch(`${apiUrl}/dietaries`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          token: token || "",
        },
        body: JSON.stringify(payload),
      });

      const responseText = await response.text();
      let result: any = null;
      try {
        result = responseText ? JSON.parse(responseText) : null;
      } catch {
        result = null;
      }

      if (!response.ok || (result && result.success === false)) {
        throw new Error(result?.message || responseText || "Failed to update dietary.");
      }

      navigate("/dietaries");
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Failed to update dietary.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!dietaryId) {
      setSubmitError("No dietary selected. Please open a dietary from the list.");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this dietary?")) return;
    if (!apiUrl) {
      setSubmitError("API URL is not configured.");
      return;
    }

    setSubmitError("");
    setDeleteLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/dietaries/${dietaryId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          token: token || "",
        },
      });

      const responseText = await response.text();
      let result: any = null;
      try {
        result = responseText ? JSON.parse(responseText) : null;
      } catch {
        result = null;
      }

      if (!response.ok || (result && result.success === false)) {
        throw new Error(result?.message || responseText || "Failed to delete dietary.");
      }

      navigate("/dietaries");
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Failed to delete dietary.");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <ComponentCard title="Update Dietary">
      <Form onSubmit={handleSubmit}>
        <div className="grid gap-6">
          <div className="col-span-full">
            <h4 className="border-b border-gray-200 pb-4 text-base font-medium text-gray-800 dark:border-gray-800 dark:text-white/90">
              Dietary
            </h4>
          </div>

          <div>
            <Label>Dietary Name</Label>
            <Input
              type="text"
              id="dietaryName"
              name="dietaryName"
              value={formData.dietaryName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFormData((prev) => ({ ...prev, dietaryName: e.target.value }));
                setErrors((prev) => ({ ...prev, dietaryName: "" }));
              }}
            />
            {errors.dietaryName && <p className="text-sm text-red-500">{errors.dietaryName}</p>}
          </div>

          <div>
            <Label>Record Status</Label>
            <Select
              options={[
                { value: "current", label: "Current" },
                { value: "non-current", label: "Non-Current" },
              ]}
              value={formData.status}
              onChange={(value: string) => setFormData((prev) => ({ ...prev, status: value }))}
              className="mt-2 bg-gray-50 dark:bg-gray-800"
            />
          </div>

          {submitError && <p className="text-sm text-red-500">{submitError}</p>}

          <div className="flex gap-3">
            <Button size="sm" type="submit" disabled={submitLoading || !dietaryId}>
              {submitLoading ? "Updating..." : "Update Changes"}
            </Button>
            <Button size="sm" variant="outline" type="button" onClick={() => navigate("/dietaries")}>
              Cancel
            </Button>
            <button
              type="button"
              className="rounded-lg bg-error-800 px-4 py-3 text-sm font-medium text-white shadow-theme-xs hover:bg-error-600 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={handleDelete}
              disabled={deleteLoading || submitLoading || !dietaryId}
            >
              {deleteLoading ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      </Form>
    </ComponentCard>
  );
}
